import { ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Heading,
  Link,
  Paragraph,
  Popover,
} from '@digdir/designsystemet-react';
import { BellIcon } from '@navikt/aksel-icons';
import { Routes } from '../../enums/Routes';
import {
  useMarkNotificationsSeenMutation,
  useNotificationCenterQuery,
} from '../../hooks/queries/notificationCenter/useNotificationCenterQuery';
import { useMarkNotificationsReadMutation } from '../../hooks/queries/safetyRep/useSafetyRepWork';
import {
  badgeCount,
  centerItems,
  seenKeys,
} from '../../pages/NotificationCenter/notificationCenterView';
import { NewsList, TaskCards } from './NotificationItems';
import classes from './NotificationCenter.module.css';

const K = 'notificationCenter';

/** What the panel shows before «Se alle varsler» */
const SHOWN = { waiting: 3, info: 3 };

/**
 * Tells a screen reader user when more comes in, as the count on the bell is only seen. Not on the first load, and not
 * when the count goes down.
 */
const useNewCountMessage = (count: number | null) => {
  const { t } = useTranslation();
  const previous = useRef<number | null>(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (count === null) return;
    if (previous.current !== null && count > previous.current) setMessage(t(`${K}.newCount`, { count }));
    previous.current = count;
  }, [count, t]);
  return message;
};

/**
 * The bell at the top right: how much is new across the modules, and the first of it in a panel. Opening it takes the
 * count away, as in other apps; what comes after counts again. What comes in while the panel is open is seen in it, so
 * it is counted as seen when the panel closes.
 */
export const NotificationBell = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isError } = useNotificationCenterQuery(i18n.language);
  const markSeen = useMarkNotificationsSeenMutation();
  const markRead = useMarkNotificationsReadMutation();
  const items = data ? centerItems(data, t, i18n.language) : null;
  const count = items && data ? badgeCount(items, data.seen) : 0;
  const announcement = useNewCountMessage(items ? count : null);
  const label = count > 0 ? t(`${K}.bellWithCount`, { count }) : t(`${K}.bell`);
  // The popover keeps the handlers from when it was first shown, so they read what is in the list now from here
  const markAllSeen = useRef(() => {});
  markAllSeen.current = () => {
    if (items && data && count > 0) markSeen.mutate(seenKeys(items, data));
  };
  // The popover opens and closes itself from the trigger, outside clicks and Escape, and tells us here
  const show = () => {
    setOpen(true);
    markAllSeen.current();
  };
  const close = () => {
    setOpen(false);
    markAllSeen.current();
  };

  return (
    <Popover.TriggerContext>
      <span className="ds-sr-only" aria-live="polite">
        {announcement}
      </span>
      <Popover.Trigger
        variant="tertiary"
        data-color="neutral"
        icon
        aria-label={label}
        title={label}
        className={classes.bell}
      >
        <Badge.Position placement="top-right" overlap="circle">
          <BellIcon aria-hidden className={classes.bellIcon} />
          {count > 0 && (
            <Badge
              count={count}
              maxCount={99}
              data-color="danger"
              data-size="sm"
            />
          )}
        </Badge.Position>
      </Popover.Trigger>
      <Popover
        placement="bottom-end"
        data-color="neutral"
        className={classes.panel}
        open={open}
        onOpen={show}
        onClose={close}
        aria-label={t(`${K}.title`)}
      >
        <div className={classes.panelBody}>
          <div className={classes.panelHeader}>
            <Heading level={2} data-size="2xs">
              {t(`${K}.title`)}
            </Heading>
            {items && items.info.length > 0 && (
              <Button
                variant="tertiary"
                data-size="sm"
                onClick={() => markRead.mutate(null)}
              >
                {t(`${K}.markAllRead`)}
              </Button>
            )}
          </div>
          {data && data.unavailable.length > 0 && (
            <Paragraph data-size="sm" className={classes.unavailable}>
              {t(`${K}.unavailable`, {
                modules: data.unavailable.map((part) => t(`${K}.sources.${part}`)).join(', '),
              })}
            </Paragraph>
          )}
          {!items ? (
            <Paragraph data-size="sm" className={classes.empty}>
              {isError ? t(`${K}.failed`) : t(`${K}.loading`)}
            </Paragraph>
          ) : items.waiting.length + items.info.length === 0 ? (
            <Paragraph data-size="sm" className={classes.empty}>
              {t(`${K}.nothing`)}
            </Paragraph>
          ) : (
            <>
              {items.waiting.length > 0 && (
                <section
                  className={classes.panelSection}
                  aria-labelledby="bell-waiting"
                >
                  <h3 id="bell-waiting" className={classes.sectionTitle}>
                    {items.waiting.length > SHOWN.waiting
                      ? t(`${K}.waitingSome`, {
                          shown: SHOWN.waiting,
                          count: items.waiting.length,
                        })
                      : t(`${K}.waitingCount`, { count: items.waiting.length })}
                  </h3>
                  <TaskCards
                    items={items.waiting.slice(0, SHOWN.waiting)}
                    onOpen={close}
                    headingLevel={4}
                  />
                </section>
              )}
              {items.info.length > 0 && (
                <section
                  className={classes.panelSection}
                  aria-labelledby="bell-news"
                >
                  <h3 id="bell-news" className={classes.sectionTitle}>
                    {t(`${K}.info`)}
                  </h3>
                  <NewsList
                    items={items.info.slice(0, SHOWN.info)}
                    onOpen={close}
                    headingLevel={4}
                  />
                </section>
              )}
            </>
          )}
          <div className={classes.panelFooter}>
            <Link asChild data-size="sm">
              <RouterLink to={Routes.Notifications} onClick={close}>
                {t(`${K}.seeAll`)}
              </RouterLink>
            </Link>
            <RouterLink
              to={`${Routes.Notifications}#innstillinger`}
              onClick={close}
              className={classes.settingsLink}
            >
              {t(`${K}.settings`)}
            </RouterLink>
          </div>
        </div>
      </Popover>
    </Popover.TriggerContext>
  );
};
